import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowRight,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  FileText,
} from "lucide-react";

// Shared building blocks for service and study detail pages

export const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

export const SectionHeading = ({ title, description }) => (
  <motion.div {...fadeUp} className="text-center mb-12">
    <h2 className="text-3xl font-bold text-gray-900 mb-4">{title}</h2>
    {description && (
      <p className="text-lg text-gray-600 max-w-3xl mx-auto">{description}</p>
    )}
  </motion.div>
);

// items: [{ label, to }]; the last item is the current page and is not linked
export const Breadcrumbs = ({ items }) => (
  <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
    <ol className="container mx-auto px-4 max-w-6xl py-3 flex flex-wrap items-center text-sm text-gray-500">
      {items.map((item, index) =>
        index < items.length - 1 ? (
          <li key={item.label} className="flex items-center">
            <RouterLink
              to={item.to}
              className="hover:text-custom-red transition-colors duration-300"
            >
              {item.label}
            </RouterLink>
            <ChevronRight className="w-4 h-4 mx-2" aria-hidden="true" />
          </li>
        ) : (
          <li
            key={item.label}
            className="text-gray-900 font-medium"
            aria-current="page"
          >
            {item.label}
          </li>
        )
      )}
    </ol>
  </nav>
);

export const Process = ({
  steps,
  title = "Our Approach",
  description = "A proven, structured process that takes every project from first conversation to final delivery.",
}) => (
  <section className="py-16 bg-white">
    <div className="container mx-auto px-4 max-w-6xl">
      <SectionHeading title={title} description={description} />
      <div
        className={`grid grid-cols-1 md:grid-cols-2 gap-8 ${
          steps.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"
        }`}
      >
        {steps.map((step, index) => (
          <motion.div
            key={index}
            {...fadeUp}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="relative"
          >
            <div className="w-12 h-12 rounded-full bg-custom-blue text-white flex items-center justify-center text-xl font-bold mb-4">
              {index + 1}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {step.title}
            </h3>
            <p className="text-gray-600">{step.description}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const FaqItem = ({ faq, isOpen, onToggle }) => (
  <div className="bg-white rounded-lg shadow border border-gray-100">
    <button
      onClick={onToggle}
      aria-expanded={isOpen}
      className="w-full flex items-center justify-between text-left p-5 font-semibold text-gray-900 hover:text-custom-blue transition-colors duration-300"
    >
      {faq.question}
      <ChevronDown
        className={`w-5 h-5 flex-shrink-0 ml-4 text-custom-blue transition-transform duration-300 ${
          isOpen ? "rotate-180" : ""
        }`}
      />
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <p className="px-5 pb-5 text-gray-600">{faq.answer}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const Faqs = ({ faqs }) => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4 max-w-4xl">
        <SectionHeading title="Frequently Asked Questions" />
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <FaqItem
              key={index}
              faq={faq}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? null : index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const ChecklistCard = ({ icon: Icon, title, items }) => (
  <motion.div
    {...fadeUp}
    className="bg-white rounded-lg shadow-lg p-8 border border-gray-100"
  >
    <div className="flex items-center mb-6">
      <div className="bg-blue-50 p-3 rounded-full text-custom-blue">
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="text-xl font-semibold text-gray-900 ml-4">{title}</h3>
    </div>
    <ul className="space-y-3">
      {items.map((item, index) => (
        <li key={index} className="flex items-start">
          <span className="text-custom-blue mr-2">✓</span>
          <span className="text-gray-700">{item}</span>
        </li>
      ))}
    </ul>
  </motion.div>
);

export const DeliverablesAndStandards = ({ deliverables, standards }) => (
  <section className="py-16 bg-gray-50">
    <div className="container mx-auto px-4 max-w-6xl grid grid-cols-1 md:grid-cols-2 gap-8">
      {deliverables && (
        <ChecklistCard
          icon={FileText}
          title="What You Receive"
          items={deliverables}
        />
      )}
      {standards && (
        <ChecklistCard
          icon={ClipboardCheck}
          title="Standards We Follow"
          items={standards}
        />
      )}
    </div>
  </section>
);

export const CallToAction = ({ title }) => (
  <section className="py-16 bg-custom-blue">
    <motion.div
      {...fadeUp}
      className="container mx-auto px-4 max-w-4xl text-center"
    >
      <h2 className="text-3xl font-bold text-white mb-4">{title}</h2>
      <p className="text-lg text-gray-200 mb-8">
        Tell us about your project and our engineers will get back to you
        within 24 hours.
      </p>
      <RouterLink
        to="/contact"
        className="inline-flex items-center bg-white hover:bg-custom-red hover:text-white text-custom-blue font-semibold py-3 px-6 rounded transition duration-300"
      >
        Request a Consultation
        <ArrowRight className="w-5 h-5 ml-2" />
      </RouterLink>
    </motion.div>
  </section>
);

// industries: [{ image, title, description }]
export const Industries = ({
  industries,
  title = "Industries We Serve",
  description = "We tailor every study to the operating realities and regulatory requirements of your industry.",
}) => (
  <section className="py-16 bg-gray-50">
    <div className="container mx-auto px-4 max-w-6xl">
      <SectionHeading title={title} description={description} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {industries.map((industry, index) => (
          <motion.div
            key={index}
            {...fadeUp}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="group relative h-80 rounded-lg shadow-lg overflow-hidden"
          >
            <img
              src={require(`../assets/images/${industry.image}`)}
              alt={industry.title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-6">
              <h3 className="text-lg font-semibold text-white mb-2">
                {industry.title}
              </h3>
              <p className="text-sm text-gray-200">{industry.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// tools: [{ name, logo? }]; logos live in assets/svgs
export const PlatformList = ({ tools, title = "Software We Use" }) => (
  <section className="py-12 bg-white">
    <div className="container mx-auto px-4 max-w-6xl">
      <motion.h2
        {...fadeUp}
        className="text-2xl font-bold text-gray-900 text-center mb-8"
      >
        {title}
      </motion.h2>
      <motion.ul {...fadeUp} className="flex flex-wrap justify-center gap-4">
        {tools.map((tool) => (
          <li
            key={tool.name}
            className="flex items-center bg-gray-50 border border-gray-100 rounded-lg px-5 py-3"
          >
            {tool.logo && (
              <img
                src={require(`../assets/svgs/${tool.logo}`)}
                alt=""
                className="h-6 w-auto max-w-[4rem] object-contain mr-3"
              />
            )}
            <span className="font-semibold text-gray-800">{tool.name}</span>
          </li>
        ))}
      </motion.ul>
    </div>
  </section>
);

// links: [{ to, label }]
export const LinkGrid = ({ title, links }) => (
  <section className="py-16 bg-white">
    <div className="container mx-auto px-4 max-w-6xl">
      <SectionHeading title={title} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {links.map((link) => (
          <RouterLink
            key={link.to}
            to={link.to}
            className="group flex items-center justify-between bg-gray-50 hover:bg-custom-blue rounded-lg p-5 border border-gray-100 transition-colors duration-300"
          >
            <span className="font-semibold text-gray-900 group-hover:text-white transition-colors duration-300">
              {link.label}
            </span>
            <ArrowRight className="w-5 h-5 text-custom-blue group-hover:text-white flex-shrink-0 ml-4 transition-colors duration-300" />
          </RouterLink>
        ))}
      </div>
    </div>
  </section>
);
